import React, { useEffect } from 'react'
import { Table, Tag, Input, Divider, Modal, Button, Descriptions, Space, Tabs } from 'antd'
import { DollarOutlined, CalendarOutlined, CheckCircleOutlined, StopOutlined, PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { CreatePlanModal } from './CreateDNPlanModal'
import "./dnMembershipManagement.scss"
import { fetchMembershipLists, createMembershipPLan, resetCreateMembershipPLan, updateMembershipPLan, resetUpdateMembershipPLan, fetchFeaturesListData, deleteMembershipPLan, resetDeleteMembershipPLan, fetchPlanDetails, resetFetchPlanDetails } from '../../../features/superAdmin/membershipManagement/membershipManagementSlice'
import { useDispatch, useSelector } from 'react-redux'
import { appDispatch, RootState } from '../../../app/store'
import { toast } from 'react-toastify'

type Feature = {
  id: number
  name: string
}

type MembershipPlan = {
  id: number
  name: string
  price: string
  currency: string
  billing_cycle: string
  is_free_trial_subscription_plan: boolean
  active_features: Feature[]
  inactive_features: Feature[]
}

export const DNMembershipManagement = () => {
  const dispatch = useDispatch<appDispatch>();
  const [searchText, setSearchText] = React.useState('')
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedPlan, setSelectedPlan] = React.useState<MembershipPlan | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false)
  const [editingPlan, setEditingPlan] = React.useState<MembershipPlan | null>(null)
  const [activeTab, setActiveTab] = React.useState<string>('active')

  useEffect(() => {
    dispatch(fetchMembershipLists({ networkTypeId: 4, name: "", isActive: true }))
    dispatch(fetchFeaturesListData({ networkTypeId: 4 }))
  }, [])

  // Helper to refresh list using current filters
  const refreshMembership = React.useCallback(() => {
    dispatch(fetchMembershipLists({ networkTypeId: 4, name: searchText || "", isActive: activeTab === 'active' }))
  }, [dispatch, searchText, activeTab])

  // Refetch when tab changes or search changes
  useEffect(() => {
    refreshMembership()
  }, [activeTab])

  useEffect(() => {
    const id = setTimeout(() => refreshMembership(), 300)
    return () => clearTimeout(id)
  }, [searchText, refreshMembership])

  const {
    membershipListData,
    isLoading
  } = useSelector((state: RootState) => state.membershipManagement.fetchMembershipLists);

  const {
    featuresListData,
    isLoading: isLoadingFeaturesList
  } = useSelector((state: RootState) => state.membershipManagement.fetchFeaturesList);

  const {
    isLoading: isCreatingPlan,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
    message: createMessage
  } = useSelector((state: RootState) => state.membershipManagement.createMembershipPLan);

  const {
    isLoading: isUpdatingPlan,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
    message: updateMessage
  } = useSelector((state: RootState) => state.membershipManagement.updateMembershipPLan);

  const {
    isLoading: isDeletingPlan,
    isSuccess: isDeleteSuccess,
    isError: isDeleteError,
    message: deleteMessage
  } = useSelector((state: RootState) => state.membershipManagement.deleteMembershipPLan);

  const {
    planDetailsData,
    isLoading: isPlanDetailsLoading,
    isSuccess: isPlanDetailsSuccess,
    isError: isPlanDetailsError,
    message: planDetailsMessage
  } = useSelector((state: RootState) => state.membershipManagement.fetchPlanDetails);

  // Handle create plan success and error states
  useEffect(() => {
    if (isCreateSuccess) {
      toast.success(createMessage || 'Plan created successfully!');
      dispatch(resetCreateMembershipPLan());
    }
    if (isCreateError) {
      toast.error(createMessage || 'Failed to create plan');
      dispatch(resetCreateMembershipPLan());
    }
  }, [isCreateSuccess, isCreateError, createMessage, dispatch]);

  // Handle update plan success and error states
  useEffect(() => {
    if (isUpdateSuccess) {
      toast.success(updateMessage || 'Plan updated successfully!');
      dispatch(resetUpdateMembershipPLan());
    }
    if (isUpdateError) {
      toast.error(updateMessage || 'Failed to update plan');
      dispatch(resetUpdateMembershipPLan());
    }
  }, [isUpdateSuccess, isUpdateError, updateMessage, dispatch]);

  // Handle delete plan success and error states
  useEffect(() => {
    if (isDeleteSuccess) {
      toast.success(deleteMessage || 'Plan deleted successfully!');
      dispatch(resetDeleteMembershipPLan());
      dispatch(fetchMembershipLists({ networkTypeId: 4, name: searchText || "", isActive: activeTab === 'active' }));
    }
    if (isDeleteError) {
      toast.error(deleteMessage || 'Failed to delete plan');
      dispatch(resetDeleteMembershipPLan());
    }
  }, [isDeleteSuccess, isDeleteError, deleteMessage, dispatch, searchText, activeTab]);

  // Handle plan details errors
  useEffect(() => {
    if (isPlanDetailsError) {
      toast.error(planDetailsMessage || 'Failed to fetch plan details');
      dispatch(resetFetchPlanDetails());
    }
  }, [isPlanDetailsError, planDetailsMessage, dispatch]);

  const handleCreatePlan = async (values: any) => {
    try {
      // Transform the form values to match the API payload structure
      const payload = {
        name: values.name,
        price: values.price,
        duration_days: 30,
        description: values.description,
        currency: values.currency,
        billing_cycle: values.billing_cycle,
        network_type_id: 4,
        is_free_trial_subscription_plan: values.is_free_trial_subscription_plan,
        is_active: values.is_active,
        feature_ids: values.feature_ids
      };

      // Dispatch the create action
      await dispatch(createMembershipPLan(payload)).unwrap();
      
      // Close modal and reset form
      setIsCreateModalOpen(false);
      setEditingPlan(null);
      // Refresh the membership list
      dispatch(fetchMembershipLists({ networkTypeId: 4, name: searchText || "", isActive: activeTab === 'active' }));
    } catch (error: any) {
      console.error('Error creating plan:', error);
      // Error handling is done in the useEffect
    }
  }

  const handleEditPlan = (plan: MembershipPlan) => {
    // fetch latest details then open modal
    dispatch(fetchPlanDetails({ planId: plan.id, networkTypeId: 4 }))
    setEditingPlan(plan)
    setIsCreateModalOpen(true)
  }

  const handleUpdatePlan = async (values: any) => {
    if (!editingPlan) return;
    
    try {
      // Transform the form values to match the API payload structure
      const payload = {
        payload:{
          name: values.name,
          price: values.price,
          duration_days: 30,
          description: values.description,
          currency: values.currency,
          billing_cycle: values.billing_cycle,
          network_type_id: values.network_type_id,
          is_free_trial_subscription_plan: values.is_free_trial_subscription_plan,
          is_active: values.is_active,
          feature_ids: values.feature_ids
        },
        planID: editingPlan.id, // This is required by the API
      };

      // Dispatch the update action
      await dispatch(updateMembershipPLan(payload)).unwrap();
      
      // Close modal and reset form
      setIsCreateModalOpen(false);
      setEditingPlan(null);
      dispatch(resetFetchPlanDetails())
      // Refresh the membership list
      dispatch(fetchMembershipLists({ networkTypeId: 4, name: searchText || "", isActive: activeTab === 'active' }));
    } catch (error: any) {
      console.error('Error updating plan:', error);
      // Error handling is done in the useEffect
    }
  }

  const handleDeletePlan = async (planId: number) => {
    try {
      await dispatch(deleteMembershipPLan({ ids: [planId] } as any)).unwrap();
    } catch (error) {
      console.error('Error deleting plan:', error)
    }
  }

  const showDeletePlanConfirm = (plan: MembershipPlan) => {
    Modal.confirm({
      title: 'Delete plan?',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete plan "${plan.name}"?`,
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okButtonProps: { danger: true },
      onOk: () => handleDeletePlan(plan.id),
    })
  }

  const handleViewPlan = (plan: MembershipPlan) => {
    dispatch(fetchPlanDetails({ planId: plan.id, networkTypeId: 4 }))
    setSelectedPlan(plan)
    setIsModalOpen(true)
  }

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false)
    setEditingPlan(null)
    dispatch(resetCreateMembershipPLan())
    dispatch(resetUpdateMembershipPLan())
    dispatch(resetFetchPlanDetails())
  }

  const columns: ColumnsType<MembershipPlan> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Price', dataIndex: 'price', key: 'price', width: 120 },
    { title: 'Currency', dataIndex: 'currency', key: 'currency', width: 120 },
    { title: 'Billing Cycle', dataIndex: 'billing_cycle', key: 'billing_cycle', width: 140 },
    {
      title: 'Free Trial',
      dataIndex: 'is_free_trial_subscription_plan',
      key: 'is_free_trial_subscription_plan',
      width: 140,
      render: (val: boolean) => (
        <Tag className={val ? 'status-badge success' : 'status-badge neutral'}>
          {val ? 'Yes' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 220,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewPlan(record)}
          >
            View
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditPlan(record)}
            className="edit-plan-btn"
          >
            Edit
          </Button>
          {activeTab==="active"&&<Button type="link" danger icon={<DeleteOutlined />} onClick={() => showDeletePlanConfirm(record)}>
            Delete
          </Button>}
        </Space>
      ),
    },
  ]

  const viewPlan: any = isPlanDetailsSuccess && planDetailsData ? planDetailsData : null  
  const editPlanData: any = isPlanDetailsSuccess && planDetailsData ? planDetailsData : null

  return (
    <div className="membership-plan-management">
      {/* // <div style={{width:"100%"}}> */}

        <div className="toolbar">
          <Input.Search
            allowClear
            placeholder="Search by name"
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(val) => setSearchText(val)}
            style={{ width: 280 }}
            className="name-search"
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreateModalOpen(true)}
            style={{ marginLeft: 'auto' }}
          >
            Create DN Plan
          </Button>
        </div>
        <Divider className="content-divider" />

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'active',
              label: `Active`,
              children: (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={membershipListData}
                  pagination={false}
                />
              )
            },
            {
              key: 'inactive',
              label: `Inactive`,
              children: (
                <Table
                  rowKey="id"
                  columns={columns}
                  dataSource={membershipListData}
                  pagination={false}
                />
              )
            }
          ]}
        />

        <Modal
          open={isModalOpen}
          onCancel={() => { setIsModalOpen(false); dispatch(resetFetchPlanDetails()) }}
          footer={null}
          className="plan-details-modal"
          title={null}
          destroyOnClose
          centered
          width={900}
          bodyStyle={{ padding: 0 }}
        >
          {viewPlan && (
            <div className="plan-details">
              <div className="plan-hero">
                <div className="title-row">
                  <div className="title-text">{viewPlan.name}</div>
                  <div className="pill-row">
                    <span className="pill pill-price"><DollarOutlined /> {viewPlan.price}</span>
                    <span className="pill pill-cycle"><CalendarOutlined /> {viewPlan.billing_cycle}</span>
                    <span className={viewPlan.is_free_trial_subscription_plan ? 'pill pill-success' : 'pill pill-neutral'}>
                      {viewPlan.is_free_trial_subscription_plan ? <CheckCircleOutlined /> : <StopOutlined />} Free Trial
                    </span>
                  </div>
                </div>
              </div>

              <div className="plan-body">
                <div className="meta-grid">
                  <div className="meta-item">
                    <div className="meta-label">Name</div>
                    <div className="meta-value">{viewPlan.name}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-label">Description</div>
                    <div className="meta-value">{viewPlan.description || '-'}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-label">Price</div>
                    <div className="meta-value">{viewPlan.price}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-label">Currency</div>
                    <div className="meta-value">{viewPlan.currency}</div>
                  </div>
                  <div className="meta-item">
                    <div className="meta-label">Billing Cycle</div>
                    <div className="meta-value">{viewPlan.billing_cycle}</div>
                  </div>
                  {/* <div className="meta-item">
                    <div className="meta-label">Duration</div>
                    <div className="meta-value">{viewPlan.duration_days} days</div>
                  </div> */}
  
                  <div className="meta-item">
                    <div className="meta-label">Free Trial</div>
                    <div className="meta-value">
                      <span className={viewPlan.is_free_trial_subscription_plan ? 'status-badge success' : 'status-badge neutral'}>
                        {viewPlan.is_free_trial_subscription_plan ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="features-grid">
                  <div className="feature-card active">
                    <div className="card-title">Active Features</div>
                    <div className="chip-list">
                      {viewPlan.active_features?.length ? (
                        viewPlan.active_features.map((f: any) => (
                          <span key={f.id} className="chip chip-active">{f.name}</span>
                        ))
                      ) : (
                        <span className="chip">None</span>
                      )}
                    </div>
                  </div>
                  <div className="feature-card inactive">
                    <div className="card-title">Inactive Features</div>
                    <div className="chip-list">
                      {viewPlan.inactive_features?.length ? (
                        viewPlan.inactive_features.map((f: any) => (
                          <span key={f.id} className="chip">{f.name}</span>
                        ))
                      ) : (
                        <span className="chip">None</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Modal>

        <CreatePlanModal
          open={isCreateModalOpen}
          onCancel={handleCloseCreateModal}
          onSubmit={editingPlan ? handleUpdatePlan : handleCreatePlan}
          loading={editingPlan ? isUpdatingPlan : isCreatingPlan}
          editingPlan={editPlanData as any}
          featuresListData={featuresListData}
        />
      
      {/* // </div> */}
    </div>
  )
}
